import type { TFunction } from "i18next";

import type {
  Language,
  OfferedInstrumentVersion,
  SettingKey,
  SettingValueJson,
  VocabularyPack,
} from "@thalamos/haven-common";

import type { TranslationKey } from "../../locales/translationKeys.js";
import { tk } from "../../locales/translationKeys.js";

/** The headings the settings table is grouped under, in the order shown. */
export const SETTING_GROUPS = [
  "starting_an_assessment",
  "reassessment",
  "records",
  "language_and_wording",
] as const;

export type SettingGroup = (typeof SETTING_GROUPS)[number];

/** Keyed by setting, so a new setting has to say where it is shown. */
export const GROUP_OF_SETTING: Record<SettingKey, SettingGroup> = {
  availableInstrumentVersions: "starting_an_assessment",
  reassessmentCarryOverDays: "reassessment",
  recordMergeEnabled: "records",
  readsApprovedAssessmentsThroughMembers: "records",
  availableLanguages: "language_and_wording",
  defaultLanguage: "language_and_wording",
  vocabularyPack: "language_and_wording",
};

/**
 * Catalogue values are stored codes, and some contain the dots a translation
 * key treats as nesting, so each is mapped to its label here rather than used
 * as part of a key.
 */
const INSTRUMENT_VERSION_LABEL: Record<
  OfferedInstrumentVersion,
  TranslationKey
> = {
  "sample/core/1.0": tk(
    "admin.configuration.settings.choices.instrumentVersions.sampleCore10",
  ),
  "sample/core/1.1": tk(
    "admin.configuration.settings.choices.instrumentVersions.sampleCore11",
  ),
  "sample/core/1.2": tk(
    "admin.configuration.settings.choices.instrumentVersions.sampleCore12",
  ),
  "sample_cans/comprehensive/1.0": tk(
    "admin.configuration.settings.choices.instrumentVersions.cansComprehensive10",
  ),
  "sample_cans/mini/1.0": tk(
    "admin.configuration.settings.choices.instrumentVersions.cansMini10",
  ),
};

const LANGUAGE_LABEL: Record<Language, TranslationKey> = {
  en: tk("admin.configuration.settings.choices.languages.en"),
  cy: tk("admin.configuration.settings.choices.languages.cy"),
};

const VOCABULARY_PACK_LABEL: Record<VocabularyPack, TranslationKey> = {
  young_person: tk(
    "admin.configuration.settings.choices.vocabularyPacks.youngPerson",
  ),
  child: tk("admin.configuration.settings.choices.vocabularyPacks.child"),
};

const CHOICE_LABELS: Record<
  SettingKey,
  Readonly<Record<string, TranslationKey>> | null
> = {
  availableInstrumentVersions: INSTRUMENT_VERSION_LABEL,
  availableLanguages: LANGUAGE_LABEL,
  defaultLanguage: LANGUAGE_LABEL,
  vocabularyPack: VOCABULARY_PACK_LABEL,
  recordMergeEnabled: null,
  readsApprovedAssessmentsThroughMembers: null,
  reassessmentCarryOverDays: null,
};

/** The unit a whole-number setting counts in, where it has one. */
export const SETTING_UNIT: Record<SettingKey, TranslationKey | null> = {
  reassessmentCarryOverDays: tk("admin.configuration.settings.units.days"),
  availableInstrumentVersions: null,
  availableLanguages: null,
  defaultLanguage: null,
  vocabularyPack: null,
  recordMergeEnabled: null,
  readsApprovedAssessmentsThroughMembers: null,
};

/**
 * A choice's label, or the stored code where none is known — a code is ugly but
 * true, where a blank would hide that something is chosen.
 */
export const describeChoice = ({
  t,
  key,
  choice,
}: {
  t: TFunction;
  key: SettingKey;
  choice: string;
}): string => {
  const label = CHOICE_LABELS[key]?.[choice];
  return label === undefined ? choice : t(label);
};

/** A value in words, whichever kind of setting it belongs to. */
export const describeSettingValue = ({
  t,
  key,
  value,
}: {
  t: TFunction;
  key: SettingKey;
  value: SettingValueJson;
}): string => {
  if (typeof value === "boolean") {
    return t(
      value
        ? tk("admin.configuration.settings.on")
        : tk("admin.configuration.settings.off"),
    );
  }

  if (typeof value === "number") {
    const unit = SETTING_UNIT[key];
    return unit === null ? String(value) : [String(value), t(unit)].join(" ");
  }

  if (Array.isArray(value)) {
    return value.length === 0
      ? t(tk("admin.configuration.settings.none"))
      : value.map((choice) => describeChoice({ t, key, choice })).join(", ");
  }

  return describeChoice({ t, key, choice: value });
};
